/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, { CancelTokenSource } from 'axios'
import BillingSynthesisNormalizer from 'normalizer/BillingSynthesisNormalizer'
import { BillsAndCreditsEntity } from 'utils/__interfaces/Billings/BillSynthesisEntity'
import { getFirstMonthAndYear } from 'utils/Common/date-utils'
import { distinctArray } from 'utils/Common/array-utils'
import { Dataset } from 'utils/__interfaces/Common/Graph'
import { MATERIALS_SERVICES_COLORS } from 'utils/__consts/Common/Color'
import InstitutionEntity from 'utils/__interfaces/Perimeter/InstitutionEntity'
import OrganizationEntity from 'utils/__interfaces/Administrations/Organizations/OrganizationEntity'
import ActivitySynthesisEntity from 'utils/__interfaces/Activities/ActivitySynthesisEntity'
import { isCancel } from './billing-services'
import { getMemberData } from './hydra-services'
import { SYN_ACTIVITY_TYPE } from './activity-synthesis-services'

interface FamillyData {
  type: string
  typeLabel: string
  yearData: number[]
}

export interface ChartData {
  label: string
  backgroundColor: string
  data: number[]
}

/**
 * Synthèse de Facturation
 */

export enum BILLING_SYNTHESIS {
  FAMILLE_DECHETS_FACTURES = 'FAMILLE_DECHETS_FACTURES',
  FAMILLE_DECHETS_AVOIRS = 'FAMILLE_DECHETS_AVOIRS',
  FAMILLE_DECHETS_RACHETES = 'FAMILLE_DECHETS_RACHETES',
}

interface BillingSynthesisTypesItem {
  name: BILLING_SYNTHESIS
  url: string
  cancelToken: CancelTokenSource
}

interface BillingSynthesisTypes {
  FAMILLE_DECHETS_FACTURES: BillingSynthesisTypesItem
  FAMILLE_DECHETS_AVOIRS: BillingSynthesisTypesItem
  FAMILLE_DECHETS_RACHETES: BillingSynthesisTypesItem
}

const BillingSynthesisTypes: BillingSynthesisTypes = {
  FAMILLE_DECHETS_FACTURES: {
    name: BILLING_SYNTHESIS.FAMILLE_DECHETS_FACTURES,
    url: '/kpi-fac-synt-fac?regroupement[]=mois&regroupement[]=fkfamilledechet',
    cancelToken: axios.CancelToken.source(),
  },
  FAMILLE_DECHETS_AVOIRS: {
    name: BILLING_SYNTHESIS.FAMILLE_DECHETS_AVOIRS,
    url: '/kpi-fac-synt-avo?regroupement[]=mois&regroupement[]=fkfamilledechet',
    cancelToken: axios.CancelToken.source(),
  },
  FAMILLE_DECHETS_RACHETES: {
    name: BILLING_SYNTHESIS.FAMILLE_DECHETS_RACHETES,
    url: '/kpi-fac-synt-brm?regroupement[]=mois&regroupement[]=fkfamilledechet',
    cancelToken: axios.CancelToken.source(),
  },
}

interface BillingSynthesisResponse {
  data: BillsAndCreditsEntity[]
  isCancled: boolean
}

export default async function getBillingSynthesisData(
  type: BILLING_SYNTHESIS,
  year: number,
  institution: InstitutionEntity[] = [],
  organization: OrganizationEntity[] = []
): Promise<BillingSynthesisResponse> {
  const response: BillingSynthesisResponse = {
    data: [],
    isCancled: false,
  }
  // preparing api call params
  const periodeDebut = getFirstMonthAndYear(year.toString())
  const periodeFin = `${year}-12`

  const url = `${process.env.REACT_APP_BASE_API_WDH_URL}/indicateurs${BillingSynthesisTypes[type].url}`

  const params = new URLSearchParams({
    periodeDebut,
    periodeFin,
  })
  if (organization.length) {
    organization.forEach((org: OrganizationEntity) => {
      params.append('idOrganisation[]', org.id)
    })
  }
  institution.forEach((insti) => {
    params.append(`fkentreprisefacturee[]`, insti.id)
  })

  if (BillingSynthesisTypes[type].cancelToken !== undefined) {
    BillingSynthesisTypes[type].cancelToken.cancel(
      'Operation canceled due to new request.'
    )
  }
  BillingSynthesisTypes[type].cancelToken = axios.CancelToken.source()

  try {
    const resp = await axios({
      method: 'GET',
      headers: {
        'content-type': 'application/x-www-form-urlencoded;charset=utf-8',
      },
      url,
      params,
      cancelToken: BillingSynthesisTypes[type].cancelToken.token,
    })

    response.data = BillingSynthesisNormalizer.normalize(
      getMemberData(resp.data)
    )
    return Promise.resolve(response)
  } catch (err) {
    if (isCancel(err)) {
      response.isCancled = true
    }
    console.error(err)
  }
  return Promise.resolve(response)
}

export function getTotalAmount(array: any) {
  return array.reduce((a: number, b: any) => a + b.amount, 0)
}

export function getTotal(array: any) {
  return array.reduce((a: number, b: any) => a + b, 0)
}

export function extractFamillies(data: BillsAndCreditsEntity[]) {
  const wasteFamillies = data.map((item) => {
    return item.familleDechetCode
  })
  return distinctArray(wasteFamillies)
}

export function extractLibelleFamillies(data: BillsAndCreditsEntity[]) {
  const wasteFamillies = data.map((item) => {
    return item.familleDechetLibelle
  })
  return distinctArray(wasteFamillies)
}
export function extractTreated(data: ActivitySynthesisEntity[]) {
  const wasteFamillies = data.map((item) => {
    return item.familleTraitementCode
  })
  return distinctArray(wasteFamillies)
}
// will be used to get id from familleDechetCode when clicking in show détails
export function extractMapIdsCodeFamillies(data: BillsAndCreditsEntity[]) {
  const wasteFamillies = data.map((item) => {
    return { code: item.familleDechetCode, id: item.fkfamilledechet }
  })
  return wasteFamillies.filter(
    (v, i, a) => a.findIndex((t) => t.code === v.code) === i
  )
}

// will be used to fill the options
export function extractFamilliesForOptions(data: BillsAndCreditsEntity[]) {
  const wasteFamillies = data.map((item) => {
    return { value: item.familleDechetCode, label: item.familleDechetLibelle }
  })
  return wasteFamillies.filter(
    (v, i, a) => a.findIndex((t) => t.value === v.value) === i
  )
}

export function extractFamilliesCreditForOptions(
  data: BillsAndCreditsEntity[]
) {
  const wasteFamillies = data.map((item) => {
    return {
      value: item.familleDechetCode,
      label: `${item.familleDechetLibelle}`,
    }
  })
  return wasteFamillies.filter(
    (v, i, a) => a.findIndex((t) => t.value === v.value) === i
  )
}

export function extractFamilliesTreatedForOptions(
  data: ActivitySynthesisEntity[]
) {
  const wasteFamillies = data.map((item) => {
    return {
      value: item.familleTraitementCode,
      label: item.familleTraitementLibelle,
    }
  })
  return wasteFamillies.filter(
    (v, i, a) => a.findIndex((t) => t.value === v.value) === i
  )
}

export function formatDataByNoFamily(
  data: ActivitySynthesisEntity[],
  type: string
): FamillyData[] {
  const result: FamillyData[] = []
  const item: FamillyData = {
    type: '',
    yearData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    typeLabel: '',
  }
  if (type === SYN_ACTIVITY_TYPE.PASSAGES) {
    data.forEach((value) => {
      item.yearData[value.mois - 1] = value.amount
    })
  } else if (type === SYN_ACTIVITY_TYPE.TRI) {
    data.forEach((value) => {
      if (value.denominateur) {
        item.yearData[value.mois - 1] =
          (value.amount / value.denominateur) * 100
      }
    })
  }
  result.push(item)
  return result
}

export function formatDataByFamillyTreated(
  data: ActivitySynthesisEntity[]
): FamillyData[] {
  const famillies = extractTreated(data)
  const result: FamillyData[] = []
  famillies.forEach((familly) => {
    const item: FamillyData = {
      type: familly,
      yearData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      typeLabel: '',
    }
    data.forEach((value) => {
      if (value.familleTraitementCode === familly) {
        item.yearData[value.mois - 1] += value.amount
        item.typeLabel = value.familleTraitementLibelle
      }
    })
    result.push(item)
  })
  return result
}

export function formatDataByFamilly(
  data: BillsAndCreditsEntity[]
): FamillyData[] {
  const famillies = extractFamillies(data)
  const result: FamillyData[] = []
  famillies.forEach((familly) => {
    const item: FamillyData = {
      type: familly,
      yearData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      typeLabel: '',
    }
    data.forEach((value) => {
      if (value.familleDechetCode === familly) {
        item.yearData[value.mois - 1] = value.amount
        item.typeLabel = value.familleDechetLibelle
      }
    })
    result.push(item)
  })
  return result
}

export function formatDataCreditByFamilly(
  data: BillsAndCreditsEntity[]
): FamillyData[] {
  const famillies = extractFamillies(data)
  const result: FamillyData[] = []
  famillies.forEach((familly) => {
    const item: FamillyData = {
      type: familly,
      yearData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      typeLabel: '',
    }
    data.forEach((value) => {
      if (value.familleDechetCode === familly) {
        item.yearData[value.mois - 1] = value.amount
        item.typeLabel = `${value.familleDechetLibelle} (Avoirs)`
      }
    })
    result.push(item)
  })
  return result
}

export function formatDataForLineChart(
  data: BillsAndCreditsEntity[],
  year: number,
  backgroundColor: string,
  borderColor: string
): Dataset {
  const item: Dataset = {
    label: String(year),
    backgroundColor,
    data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    borderColor,
  }
  data.forEach((value) => {
    if (value.annee === year) {
      item.data[value.mois - 1] = value.amount
    }
  })
  return item
}
export function formatCreditDataForLineChart(
  data: BillsAndCreditsEntity[],
  year: number
): number[] {
  const item = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
  data.forEach((value) => {
    if (value.annee === year) {
      item[value.mois - 1] = value.amount
    }
  })
  return item
}

export function addCreditDataToLineChart(
  dataFact: number[],
  dataCredit: number[]
): number[] {
  let item = []
  item = dataFact.map((value, i) => value + dataCredit[i])
  return item
}

export function prepareDataforchart(
  data: FamillyData[],
  famillies: string[]
): ChartData[] {
  return data
    .filter((value) => {
      return famillies.includes(value.type)
    })
    .map((item) => {
      return {
        label: item.typeLabel,
        backgroundColor: MATERIALS_SERVICES_COLORS[item.type],
        data: item.yearData,
      }
    })
}

export function prepareDataforchartWithNoFamily(
  data: FamillyData[]
): ChartData[] {
  return data.map((item) => {
    return {
      label: item.typeLabel,
      backgroundColor: MATERIALS_SERVICES_COLORS[item.type],
      data: item.yearData,
    }
  })
}
